import {
  Alert,
  Button,
  HStack,
  Stack,
  Text,
  RadioGroup,
  Input,
  Box,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useMemo, useState } from "react";
import { useStores } from "../../../../shared/stores/RootStore";
import { CustomDialog } from "../../../../shared/views/CustomDialog";
import { importCodingCsv } from "../../../../project/utils/import-coding-csv";
import { OsaNodeCodingApi } from "../../../../coding/services/OsaNodeCodingApi";
import { OsaNodeCoding } from "../../../../coding/models/OsaNodeCoding";
import { toast } from "../../../../shared/views/CustomToaster";

interface Props {
  onClose: () => void;
}

const CsvImportModalBase: React.FC<Props> = ({ onClose }) => {
  const { osaWayStore, osaNodeStore, codingStore } = useStores();
  const ways = osaWayStore.ways;

  const [selectedOsaWayId, setSelectedOsaWayId] = useState<string>();
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const waysWithNodes = useMemo(
    () =>
      ways.filter((way) => {
        const nodes = osaNodeStore.nodesByWayId.get(way.id) ?? [];
        return nodes.length > 0;
      }),
    [ways, osaNodeStore.nodesByWayId],
  );

  const selectedWay = useMemo(
    () => waysWithNodes.find((way) => way.id === selectedOsaWayId),
    [waysWithNodes, selectedOsaWayId],
  );

  const submit = async () => {
    if (!selectedWay || !file) return;

    setErrors([]);
    setIsSubmitting(true);

    try {
      const nodes = osaNodeStore.nodesByWayId.get(selectedWay.id) ?? [];
      const codingsMap =
        codingStore.allNodeCodingsByWayId.get(selectedWay.id) ?? new Map();

      const codingsByNodeId: Record<string, OsaNodeCoding> = {};
      codingsMap.forEach((coding, nodeId) => {
        codingsByNodeId[nodeId] = coding;
      });

      const result = await importCodingCsv({
        file,
        selectedWay,
        nodes,
        codingsByNodeId,
      });

      if (result.errors.length > 0) {
        setErrors(result.errors);
        return;
      }

      if (result.entries.length === 0) {
        setErrors(["The file contained no importable rows."]);
        return;
      }
      await OsaNodeCodingApi.batchUpdate(result.entries);
      toast("success", "Coding saved");
      onClose();
    } catch (error) {
      console.error(error);
      setErrors(["Something went wrong while importing the CSV file."]);
    } finally {
      osaWayStore.refreshOsaWay(selectedOsaWayId!!);
      setIsSubmitting(false);
    }
  };

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header="Import data"
      footer={
        <HStack>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button
            colorPalette="primary"
            onClick={submit}
            disabled={!selectedOsaWayId || !file || isSubmitting}
            loading={isSubmitting}
          >
            Import File
          </Button>
        </HStack>
      }
    >
      <Stack gap={4}>
        <Text fontWeight="medium">Select a route</Text>

        <RadioGroup.Root
          value={selectedOsaWayId}
          onValueChange={({ value }) => {
            setSelectedOsaWayId(value!!);
          }}
          size="sm"
          colorPalette="primary"
        >
          {waysWithNodes.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">
              No routes with loaded nodes are available for import.
            </Text>
          ) : (
            <Stack gap={2}>
              {waysWithNodes.map((way) => (
                <RadioGroup.Item key={way.id} value={way.id}>
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText>{way.name}</RadioGroup.ItemText>
                </RadioGroup.Item>
              ))}
            </Stack>
          )}
        </RadioGroup.Root>

        <Box>
          <Text mb={2} fontWeight="medium">
            Upload CSV
          </Text>
          <Input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const nextFile = e.target.files?.[0] ?? null;
              setFile(nextFile);
            }}
          />
          {file && (
            <Text mt={2} fontSize="sm" color="fg.muted">
              Selected file: {file.name}
            </Text>
          )}
        </Box>

        {errors.length > 0 && (
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Import failed</Alert.Title>
              <Alert.Description>
                <Stack gap={1} mt={2}>
                  {errors.slice(0, 10).map((error, index) => (
                    <Text key={`${error}-${index}`} fontSize="sm">
                      {error}
                    </Text>
                  ))}
                  {errors.length > 10 && (
                    <Text fontSize="sm">
                      ...and {errors.length - 10} more errors
                    </Text>
                  )}
                </Stack>
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}
      </Stack>
    </CustomDialog>
  );
};

export const CsvImportModal = observer(CsvImportModalBase);
